import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import UserIdentitiesService from "@/services/UserIdentitiesService";
import SuitabilityTestsService from "@/services/SuitabilityTestsService";
import { getLatestSignedSuitabilityTest } from "@/screen/ContractManagement/helpers/suitabilityTestValidity";
import { buildIdentitiesListNavigationState, IdentitiesPath, type SuitabilityTestRenewalNavigationState } from "@/screen/Identities/identitiesRoutes";
import { resolveSuitabilityTestError } from "@/components/SuitabilityTests/suitabilityTestErrorMessages";
import { buildIntervenerDisplayName, buildSuitabilityRenewalView, type SuitabilityRenewalView } from "@/screen/Identities/SuitabilityTestRenewal/suitabilityRenewalView";

export interface SuitabilityTestRenewalController
{
    userIdentityId?: number;
    displayName: string;
    isLoading: boolean;
    loadError: string | null;
    view: SuitabilityRenewalView | null;
    goBack: () => void;
    leaveBecauseOfLimit: (message: string) => void;
}

/**
 * Carga el test firmado más reciente del interviniente y lo casa con el cuestionario vigente.
 * No hay plan: la renovación va por identidad (N1-bis). Si el id no llega o no hay test firmado, vuelve al listado.
 * Un test vigente se puede actualizar. Un 409 de los 30 días vuelve al listado con la fecha.
 */
export function useSuitabilityTestRenewalController(): SuitabilityTestRenewalController
{
    const history = useHistory();
    const location = useLocation<SuitabilityTestRenewalNavigationState>();
    const userIdentityId = location.state?.userIdentityId;
    const userIdentityNameFromNav = location.state?.userIdentityName;

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [view, setView] = useState<SuitabilityRenewalView | null>(null);
    const [displayName, setDisplayName] = useState<string>(userIdentityNameFromNav?.trim() ?? "");

    const goBack = (): void =>
    {
        history.push(IdentitiesPath);
    };

    const leaveBecauseOfLimit = (message: string): void =>
    {
        history.replace(IdentitiesPath, buildIdentitiesListNavigationState(undefined, undefined, message));
    };

    useEffect(() =>
    {
        if(!userIdentityId || userIdentityId <= 0)
        {
            history.replace(IdentitiesPath);
        }
    }, [userIdentityId, history]);

    useEffect(() =>
    {
        if(!userIdentityId || userIdentityId <= 0)
        {
            return;
        }

        let cancelled = false;

        const load = async (): Promise<void> =>
        {
            setIsLoading(true);
            setLoadError(null);
            try
            {
                const [identity, currentVersion] = await Promise.all(
                [
                    UserIdentitiesService.getUserIdentityWithSuitabilityTests(userIdentityId),
                    SuitabilityTestsService.getLastSuitabilityTestVersion(),
                ]);
                if(cancelled)
                {
                    return;
                }

                const latest = getLatestSignedSuitabilityTest(identity?.suitabilityTests);
                if(!latest || !currentVersion)
                {
                    history.replace(IdentitiesPath);
                    return;
                }

                // El listado de tests no trae el JSON del cuestionario antiguo: solo se pide si la versión cambió.
                let previousVersion = currentVersion;
                if(latest.suitabilityTestVersionId && latest.suitabilityTestVersionId !== currentVersion?.id)
                {
                    previousVersion = await SuitabilityTestsService.getSuitabilityTestVersion(latest.suitabilityTestVersionId);
                }
                if(cancelled)
                {
                    return;
                }

                const loadedName = buildIntervenerDisplayName(identity?.firstName, identity?.lastName, identity?.secondLastName);
                setDisplayName(userIdentityNameFromNav?.trim() ? userIdentityNameFromNav : loadedName);

                const renewalView = buildSuitabilityRenewalView(identity?.suitabilityTests, currentVersion, previousVersion);
                if(renewalView.redirectToList)
                {
                    history.replace(IdentitiesPath);
                    return;
                }

                setView(renewalView);
            }
            catch(requestError)
            {
                if(!cancelled)
                {
                    setLoadError(resolveSuitabilityTestError(requestError, "No se ha podido cargar el test de idoneidad. Inténtalo de nuevo."));
                }
            }
            finally
            {
                if(!cancelled)
                {
                    setIsLoading(false);
                }
            }
        };

        void load();

        return () =>
        {
            cancelled = true;
        };
    }, [userIdentityId, history, userIdentityNameFromNav]);

    return{
        userIdentityId,
        displayName,
        isLoading,
        loadError,
        view,
        goBack,
        leaveBecauseOfLimit,
    };
}
